import { feverApiFetchOpts, fetches, methods } from 'api/commom';
import queryString from 'query-string';
import {
    UPDATE_ACHIEVEMENT_LEVEL_ERROR,
    UPDATE_ACHIEVEMENT_SETTING_ERROR
} from 'apiHandler/achievement';
import { ACHIEVEMENT_SEARCH_TYPE } from 'features/achievement/config';

/**
 * 建立成就計劃 API
 * @description 建立新的成就計劃，包含基本設定和配置
 * @see https://app.clickup.com/t/86eunaxb1
 * @param {Object} params - 參數物件
 * @param {string} params.sponsorId - Sponsor ID
 * @param {Object} params.body - API 請求內容，包含成就計劃的詳細設定
 * @returns {Promise<Object>} Return Promise
 */
export function createAchievementApi({ sponsorId, body }) {
    return fetches(
        `/services/achievements/sponsors/${sponsorId}`,
        feverApiFetchOpts({
            method: methods.POST,
            body
        })
    ).then(resp => resp);
}

/**
 * 更新成就計劃 API
 * @description 更新現有成就計劃的基本資訊和設定
 * @see https://app.clickup.com/t/86eunaxe5
 * @param {Object} params - 參數物件
 * @param {string} params.sponsorId - Sponsor ID
 * @param {string} params.achievementUUID - 成就計劃 UUID
 * @param {Object} params.body - 要更新的成就計劃資料
 * @returns {Promise<Object>} Return Promise
 */
export const updateAchievementApi = ({ sponsorId, achievementUUID, body }) => {
    return fetches(
        `/services/achievements/sponsors/${sponsorId}/${achievementUUID}`,
        feverApiFetchOpts({
            method: methods.PATCH,
            body
        })
    );
};

/**
 * 更新成就計劃設定 API
 * @description 更新成就計劃的進階設定，如顯示設定、權限設定等
 * @see https://app.clickup.com/t/86eupnym6
 * @param {Object} params - 參數物件
 * @param {string} params.sponsorId - Sponsor ID
 * @param {string} params.achievementUUID - 成就計劃 UUID
 * @param {Object} params.body - 要更新的設定資料
 * @returns {Promise<Object>} Return Promise
 * @throws {Error} 當設定更新失敗時拋出錯誤
 */
export const updateAchievementSettingApi = ({ sponsorId, achievementUUID, body }) => {
    return fetches(
        `/services/achievements/sponsors/${sponsorId}/${achievementUUID}/settings`,
        feverApiFetchOpts({
            method: methods.PATCH,
            body
        }),
        {
            throwErrConfigs: UPDATE_ACHIEVEMENT_SETTING_ERROR
        }
    );
};

/**
 * 取得成就計劃列表 API
 * @description 取得指定 Sponsor 下的所有成就計劃列表，支援分頁和篩選
 * @see https://app.clickup.com/t/86eunaxgp
 * @param {Object} params - 參數物件
 * @param {string} params.sponsorId - Sponsor ID
 * @param {string} [params.loginMethod] - 登入方式篩選
 * @param {boolean} [params.withLevels] - 是否包含等級資訊
 * @param {number} [params.page] - 頁碼
 * @param {number} [params.perPage=10] - 每頁顯示數量
 * @returns {Promise<Object>} Return Promise
 */
export const getAchievementListApi = ({
    sponsorId,
    loginMethod,
    withLevels,
    page,
    perPage = 10
}) => {
    const qs = queryString.stringify({ loginMethod, withLevels, page, perPage });
    return fetches(
        `/services/achievements/sponsors/${sponsorId}${Boolean(qs) ? `?${qs}` : ``}`,
        feverApiFetchOpts()
    );
};

/**
 * 取得成就計劃設定 API
 * @description 取得指定成就計劃的詳細設定資訊
 * @see https://app.clickup.com/t/86euqnjvx
 * @param {Object} params - 參數物件
 * @param {string} params.sponsorId - Sponsor ID
 * @param {string} params.achievementUUID - 成就計劃 UUID
 * @returns {Promise<Object>} Return Promise
 */
export const getAchievementSettingApi = ({ sponsorId, achievementUUID }) => {
    return fetches(
        `/services/achievements/sponsors/${sponsorId}/${achievementUUID}/settings`,
        feverApiFetchOpts()
    );
};

/**
 * 取得成就計劃統計資料 API
 * @description 取得成就計劃的參與統計資料，包含總參與人數和各等級參與人數
 * @see https://app.clickup.com/t/86eunaxm3
 * @param {Object} params - 參數物件
 * @param {string} params.sponsorId - Sponsor ID
 * @param {string} params.achievementUUID - 成就計劃 UUID
 * @returns {Promise<Object>} Return Promise
 */
export const getAchievementStatisticsApi = ({ sponsorId, achievementUUID }) => {
    return fetches(
        `/services/achievements/sponsors/${sponsorId}/${achievementUUID}/statistics/levels`,
        feverApiFetchOpts()
    );
};

/**
 * 取得成就計劃會員列表 API ( Nice To Have )
 * @description 取得參與指定成就計劃的會員列表，此功能為額外需求
 * @see https://app.clickup.com/t/86eunaxrh
 * @param {Object} params - 參數物件
 * @param {string} params.sponsorId - Sponsor ID
 * @param {string} params.achievementUUID - 成就計劃 UUID
 * @returns {Promise<Object>} Return Promise
 */
export const getAchievementMembersApi = ({ sponsorId, achievementUUID }) => {
    console.log('getAchievementMembersApi:', { sponsorId, achievementUUID });
    // return fetches(
    //     `/services/achievements/sponsors/${sponsorId}/${achievementId}/members`,
    //     feverApiFetchOpts()
    // );
};

/**
 * 建立成就計劃等級 API
 * @description 為指定成就計劃建立新的等級任務
 * @see https://app.clickup.com/t/86eunaxvp
 * @param {Object} params - 參數物件
 * @param {string} params.sponsorId - Sponsor ID
 * @param {string} params.achievementUUID - 成就計劃 UUID
 * @param {Object} params.body - 等級任務的詳細設定
 * @returns {Promise<Object>} Return Promise
 */
export const createAchievementLevelApi = ({ sponsorId, achievementUUID, body }) => {
    return fetches(
        `/services/achievements/sponsors/${sponsorId}/${achievementUUID}/task`,
        feverApiFetchOpts({
            method: methods.POST,
            body
        })
    );
};

/**
 * 取得成就計劃等級列表 API
 * @description 取得指定成就計劃下的所有等級任務列表
 * @see https://app.clickup.com/t/86eunaxxp
 * @param {Object} params - 參數物件
 * @param {string} params.sponsorId - Sponsor ID
 * @param {string} params.achievementUUID - 成就計劃 UUID
 * @returns {Promise<Object>} Return Promise
 */
export const getAchievementLevelListApi = ({ sponsorId, achievementUUID }) => {
    return fetches(
        `/services/achievements/sponsors/${sponsorId}/${achievementUUID}/levels`,
        feverApiFetchOpts()
    );
};

/**
 * 更新成就計劃等級 API
 * @description 更新指定成就計劃中的特定等級任務
 * @see https://app.clickup.com/t/86eunay02
 * @param {Object} params - 參數物件
 * @param {string} params.sponsorId - Sponsor ID
 * @param {string} params.achievementUUID - 成就計劃 UUID
 * @param {string} params.levelUUID - 等級任務的唯一識別碼
 * @param {Object} params.body - 要更新的等級任務資料
 * @returns {Promise<Object>} Return Promise
 * @throws {Error} 當等級更新失敗時拋出錯誤
 */
export const updateAchievementLevelApi = ({ sponsorId, achievementUUID, levelUUID, body }) => {
    return fetches(
        `/services/achievements/sponsors/${sponsorId}/${achievementUUID}/levels/${levelUUID}`,
        feverApiFetchOpts({
            method: methods.PATCH,
            body
        }),
        {
            throwErrConfigs: UPDATE_ACHIEVEMENT_LEVEL_ERROR
        }
    );
};

/**
 * 刪除成就計劃等級 API
 * @description 刪除指定成就計劃中的特定等級任務
 * @see https://app.clickup.com/t/86eunay5m
 * @param {Object} params - 參數物件
 * @param {string} params.sponsorId - Sponsor ID
 * @param {string} params.achievementUUID - 成就計劃 UUID
 * @param {string} params.levelUUID - 要刪除的等級任務唯一識別碼
 * @returns {Promise<Object>} Return Promise
 */
export const deleteAchievementLevelApi = ({ sponsorId, achievementUUID, levelUUID }) => {
    return fetches(
        `/services/achievements/sponsors/${sponsorId}/${achievementUUID}/levels/${levelUUID}`,
        feverApiFetchOpts({
            method: methods.DELETE
        })
    );
};

/**
 * 更新成就計劃狀態 API
 * @description 更新成就計劃的啟用狀態，控制成就計劃是否對外開放
 * @param {Object} params - 參數物件
 * @param {string} params.sponsorId - Sponsor ID
 * @param {string} params.achievementUUID - 成就計劃 UUID
 * @param {number} params.status - 狀態碼：未啟用(0) / 啟用(1) / 停用(2)
 * @returns {Promise<Object>} Return Promise
 */
export const updateAchievementStatusApi = ({ sponsorId, achievementUUID, status }) => {
    return fetches(
        `/services/achievements/sponsors/${sponsorId}/${achievementUUID}/status`,
        feverApiFetchOpts({
            method: methods.PATCH,
            body: { status }
        })
    );
};

/**
 * 取得成就計劃用戶清單 API
 * @description 取得參與指定成就計劃的用戶清單，支援分頁載入
 * @param {Object} params - 參數物件
 * @param {string} params.sponsorId - Sponsor ID
 * @param {string} params.achievementUUID - 成就計劃 UUID
 * @param {number} [params.page=1] - 頁碼，預設為第1頁
 * @param {number} [params.perPage=20] - 每頁筆數
 * @param {number} [params.searchType] - 搜尋類型 (1:用戶名稱, 2:會員UID)
 * @param {string} [params.searchKey] - 搜尋關鍵字
 * @param {string} [params.levelUUID] - 等級篩選
 * @returns {Promise<Object>} Return Promise
 */
export const getAchievementUserListApi = ({
    sponsorId,
    achievementUUID,
    page = 1,
    perPage = 20,
    searchType,
    searchKey,
    levelUUID
}) => {
    const getQs = () => {
        const params = { page, perPage };

        if (levelUUID) {
            params.levelUuid = levelUUID;
        }

        if (searchType && searchKey) {
            if (searchType === ACHIEVEMENT_SEARCH_TYPE.MEMBER_NAME) {
                params.memberName = searchKey;
            } else if (searchType === ACHIEVEMENT_SEARCH_TYPE.MEMBER_ID) {
                params.memberId = searchKey;
            }
        }

        return queryString.stringify(params, {
            skipNull: true,
            skipEmptyString: true
        });
    };

    return fetches(
        `/services/achievements/sponsors/${sponsorId}/${achievementUUID}/member-cards?${getQs()}`,
        feverApiFetchOpts()
    );
};

/**
 * 取得用戶等級異動紀錄 API
 * @param {Object} params - 參數物件
 * @param {string} params.sponsorId - Sponsor ID
 * @param {string} params.achievementUUID - 成就計劃 UUID
 * @param {string} params.userId - 用戶 ID
 * @param {number} [params.page=1] - 頁碼
 * @param {number} [params.perPage=20] - 每頁筆數
 * @param {number} [params.isTest=false] - 是否為測試資料
 * @returns {Promise<Object>} Return Promise
 */

export const getAchievementUserLevelHistoryApi = ({
    sponsorId,
    achievementUUID,
    memberUuid,
    page = 1,
    perPage = 20,
    isTest
}) => {
    const qs = queryString.stringify({ page, perPage, isTest });

    return fetches(
        `/services/achievements/sponsors/${sponsorId}/${achievementUUID}/member-cards/${memberUuid}/levels?${qs}`,
        feverApiFetchOpts()
    );
};
